import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { ChatKeys } from "web_myplann/Api/Chat/ChatKeys";
import {
	limit,
	useResetInfiniteData,
} from "web_myplann/Api/infiniteQueryUtils";
import { getQueuedChatClientIds } from "web_myplann/Chat/Helpers/Messages/getQueuedChatClientIds";
import { getUnsentChatMessages } from "web_myplann/Chat/Helpers/Messages/getUnsentChatMessages";
import { mapChatMessage } from "web_myplann/Chat/Helpers/Messages/mapChatMessage";
import type { ChatMessagesPage } from "web_myplann/Chat/Types/chatMessagesPage";
import { getChatsConversationsIdMessagesV2 } from "web_myplann/Infrastructure/Api/Api";
import { processResponseQuery } from "web_myplann/Infrastructure/Api/ApiUtils";

const FIRST_CURSOR = null;

export const useChatMessagesQuery = (conversationId: number | null) => {
	const queryClient = useQueryClient();

	useResetInfiniteData(ChatKeys.messages(conversationId));

	return useInfiniteQuery({
		queryKey: ChatKeys.messages(conversationId),
		queryFn: async (ctx): Promise<ChatMessagesPage> => {
			const response = await processResponseQuery(() =>
				getChatsConversationsIdMessagesV2(
					conversationId!,
					ctx.pageParam ?? undefined,
					limit,
				),
			)(ctx);
			const page = response?.data ?? [];
			const items = [...page].reverse().map(mapChatMessage);
			const pageSize = response?.meta?.limit ?? limit;
			const nextCursor =
				page.length < pageSize ? null : (page.at(-1)?.id ?? null);

			if (ctx.pageParam !== FIRST_CURSOR) {
				return { items, nextCursor };
			}

			const unsent = getUnsentChatMessages(
				queryClient,
				conversationId,
				getQueuedChatClientIds(queryClient, conversationId),
			);

			return { items: [...items, ...unsent], nextCursor };
		},
		enabled: conversationId !== null,
		select: (data) => data.pages.flatMap((page) => page.items),
		meta: { persist: false },
		getNextPageParam: (lastPage: ChatMessagesPage) => lastPage.nextCursor,
		initialPageParam: FIRST_CURSOR as number | null,
	});
};
